/**
 * O par registrar/desregistrar dos QUATRO provedores que `view-chat.vue`
 * injeta no motor de chat — saiu da view para ela caber no teto de linhas do
 * repo, e para o contrato (registra no `created`, desfaz no `beforeDestroy`,
 * com as MESMAS referências) ter dono e teste próprios.
 *
 * | provedor | o que entrega | por quê é injetado |
 * |---|---|---|
 * | `contextoDeTela` | `{rota_atual, filtros_da_tela}` no corpo do POST (US-5) | `apiChat.js` não pode importar `@router` — fecha o ciclo que quebra o `extends APIChat` do fee-fixo (`utils/contexto-de-tela.js`); o provedor em si vem de `utils/provedor-de-contexto-de-tela.js` |
 * | `contextoDeExecucao` | `{vm, router, store, canAccessRoute}` das tools de front (US-4) | separado do de tela porque aquele é SERIALIZADO — o router não pode ir junto |
 * | `onTextoDelta` | cada delta de texto do turno → fala progressiva do Modo de Voz | o motor decide sozinho se está num turno de voz (no-op fora dele), então registra incondicionalmente |
 * | `onEventoFala` | o evento SSE `fala {texto}` → canal PRIMÁRIO da fala (o delta vira fallback, ver `fala-da-ia.js`) | mesma régua incondicional do de cima |
 *
 * Quem confere "ainda são os desta view?" é o próprio `apiChat`
 * (`desregistrarProvedores`, guard de identidade): na troca de rota o
 * `created` da view nova roda ANTES do `beforeDestroy` da velha, e zerar sem
 * conferir apagaria o registro recém-feito.
 */
import { provedorDeContextoDeTela } from '@modules/aawz-intelligence-v2/utils/provedor-de-contexto-de-tela'
import { provedorDeContextoDeExecucao } from '@modules/aawz-intelligence-v2/utils/contexto-de-execucao'
import {
  processarDeltaDeFala,
  processarEventoFala,
} from '@modules/aawz-intelligence-v2/utils/motor-de-voz'

// O namespace da única superfície que registra por aqui (`view-chat.vue`).
const STORE_NAMESPACE = 'aawz-intelligence-v2'

/** Os quatro provedores fechados sobre `vm` — referências únicas desta view. */
function montarProvedoresDoApiChat(vm, storeNamespace = STORE_NAMESPACE) {
  return {
    contextoDeTela: provedorDeContextoDeTela(vm, storeNamespace),
    contextoDeExecucao: provedorDeContextoDeExecucao(vm),
    onTextoDelta: (delta) => processarDeltaDeFala(delta),
    onEventoFala: (texto) => processarEventoFala(texto),
  }
}

/**
 * Registra os quatro no motor e DEVOLVE o objeto — a view o guarda para o
 * `beforeDestroy` desregistrar exatamente o que registrou.
 */
function registrarProvedoresDoApiChat(
  api,
  vm,
  storeNamespace = STORE_NAMESPACE
) {
  const provedores = montarProvedoresDoApiChat(vm, storeNamespace)
  api.setContextoDeTelaProvider(provedores.contextoDeTela)
  api.setContextoDeExecucaoProvider(provedores.contextoDeExecucao)
  api.setOnTextoDelta(provedores.onTextoDelta)
  api.setOnEventoFala(provedores.onEventoFala)
  return provedores
}

/**
 * Registra SÓ os DOIS provedores de CONTEXTO (tela e execução) e devolve o
 * objeto, no mesmo contrato do irmão acima — a superfície o guarda para
 * `desregistrarProvedoresDoApiChat` desfazer exatamente o que registrou.
 *
 * É para a superfície SEM Modo de Voz: `registrarProvedoresDoApiChat` registra
 * também `onTextoDelta`/`onEventoFala`, os dois canais de fala, que ali não têm
 * consumidor. Consumidor de hoje: a aba de IA do módulo de comissões.
 *
 * ★ Por que existir em vez de cada tela montar os dois à mão: registro manual
 * é import direto de `provedor-de-contexto-de-tela.js`, e foi exatamente assim
 * que a aba de comissões ficou para trás quando o símbolo mudou de arquivo em
 * 14/09/2026 — os quatro importadores que já viviam no `desenvolvimento` foram
 * atualizados no mesmo PR (#2261), um por arquivo; esta aba vivia numa branch
 * paralela e ficou apontando para o caminho antigo, quebrando no merge com
 * `created` estourando ANTES de atribuir os provedores (nenhum registrado,
 * `rota_atual: null`). Quem registra por aqui herda a próxima mudança de graça.
 *
 * ⚠️ Três superfícies continuam registrando à mão — as duas do Fee Fixo e a
 * `ai-framework/views/agent-chat.vue`, que ainda por cima nunca desregistra
 * (é a mais exposta). Migrá-las para cá é follow-up de PR próprio.
 */
function registrarProvedoresDeContexto(api, vm, storeNamespace) {
  const provedores = {
    contextoDeTela: provedorDeContextoDeTela(vm, storeNamespace),
    contextoDeExecucao: provedorDeContextoDeExecucao(vm),
  }
  api.setContextoDeTelaProvider(provedores.contextoDeTela)
  api.setContextoDeExecucaoProvider(provedores.contextoDeExecucao)
  return provedores
}

/**
 * Desfaz o registro — exceto com a continuação de uma tool de front em voo:
 * o POST dela ainda lê o contexto de tela ([T024]).
 *
 * Serve aos DOIS registros: `desregistrarProvedores` do `apiChat` confere
 * campo a campo, e chave ausente no objeto é simplesmente não zerada.
 */
function desregistrarProvedoresDoApiChat(api, provedores) {
  if (api.temContinuacaoDeToolDeFront()) return
  api.desregistrarProvedores(provedores)
}

export {
  montarProvedoresDoApiChat,
  registrarProvedoresDoApiChat,
  registrarProvedoresDeContexto,
  desregistrarProvedoresDoApiChat,
}
